/**
 * UnlockModal — the friendly upgrade takeover shown when a locked profile taps
 * a gated action. Ported from the prototype's ``UnlockModal``.
 *
 * Reasons: ``create`` · ``avatar`` · ``remix`` · ``locked`` (1:1 with the gated
 * actions). Variants: ``cocoon`` (centered storybook moment) and ``split``
 * (sell on the left, checkout on the right).
 *
 * Presentational: all billing decisions go through ``useBilling``. When a card
 * is already on file we offer a one-tap reactivate (backend charges the stored
 * card); otherwise we mount the full ``CheckoutPanel`` (SetupIntent flow).
 *
 * Built on the shared ``PanelSurface`` chrome primitive (scrim + aria-modal +
 * Escape handling). Colors/radius/shadow come from ChromeTokens.
 *
 * A never-trialed family on the web (the operator's call, 2026-10-27, option 2;
 * design-trial-sheet.md): the trial takes the card up front, and the way out is a real
 * button the same size, "Keep playing free", which starts nothing and closes the sheet.
 * There is no no-card trial here any more. When the gated action has a fallback (the share
 * page's Remix saves the project, U12) the button says so; BillingChrome runs that
 * fallback on every close without a subscribe, so no way out of the sheet drops the save.
 */

import { useRef, useState } from "react";
import { PanelSurface } from "../chrome/PanelSurface";
import { ChromeTokens } from "../chrome/tokens";
import { Icon, type IconName } from "../chrome/Icon";
import { PlanPicker } from "./PlanPicker";
import { CheckoutPanel } from "./CheckoutPanel";
import { StripeSecured } from "./StripeSecured";
import { useBilling } from "./useBilling";
import { money, planById } from "./plans";
import { ensureBillingKeyframes } from "./keyframes";
import type { PlanId } from "./types";
import { useWebKeyboardHeight } from "../chrome/webKeyboardHeight";
import { billingPlatform } from "./platform";
import { AppStorePlans } from "./AppStorePlans";

export type UnlockReason = "create" | "avatar" | "remix" | "locked";
export type UnlockVariant = "cocoon" | "split";

interface UnlockCopy {
  eyebrow: string;
  title: string;
  sub: string;
  icon: IconName;
}

const UNLOCK_COPY: Record<UnlockReason, UnlockCopy> = {
  locked: {
    eyebrow: "Keep creating",
    title: "Ready to build again?",
    sub: "Your worlds are safe. Subscribe to make new creations, new avatars, and keep growing your town.",
    icon: "sparkles",
  },
  create: {
    eyebrow: "New creation",
    title: "Let's build something new",
    sub: "Making new games, stories and animations is part of a subscription. Pick up right here once you're in.",
    icon: "wand-sparkles",
  },
  avatar: {
    eyebrow: "Avatar Studio",
    title: "Make a brand-new you",
    sub: "Designing a fresh avatar comes with a subscription. You can still wear any look you've already saved.",
    icon: "palette",
  },
  remix: {
    eyebrow: "Remix",
    title: "Make this yours",
    sub: "Remixing a creation into your own copy comes with a subscription. Keep playing this one anytime.",
    icon: "copy",
  },
};

export const TRIAL_LINE = "Your free account already plays, saves and shares. The trial is for making your own.";
export const KEEP_PLAYING = "Not now, just explore";
export const KEEP_PLAYING_LINE = "Play and save free. No creating.";
export const SAVE_FOR_LATER_LINE = "We'll save this one for later";

const SELL_POINTS = [
  "Make new games, stories & animations",
  "Design brand-new avatars",
  "Everything stays safe & parent-controlled",
];

export interface UnlockModalProps {
  open: boolean;
  reason?: UnlockReason;
  variant?: UnlockVariant;
  onClose: () => void;
  onSubscribed?: () => void;
  /** The gated action has a fallback that keeps the project (U12): "Keep playing free" says so. */
  savesForLater?: boolean;
}

export function UnlockModal({
  open,
  reason = "locked",
  variant = "cocoon",
  onClose,
  onSubscribed,
  savesForLater = false,
}: UnlockModalProps): JSX.Element | null {
  const b = useBilling();
  const [pid, setPid] = useState<PlanId>(b.view?.planId ?? "family");
  const [useNewCard, setUseNewCard] = useState(false);
  const [pending, setPending] = useState(false);
  // State reaches the button on the next render; a second tap can land before it.
  const inFlight = useRef(false);
  const [footer, setFooter] = useState<HTMLDivElement | null>(null);
  useWebKeyboardHeight(open && billingPlatform().manages);
  ensureBillingKeyframes();

  if (!open) return null;

  const copy = UNLOCK_COPY[reason];
  const hasCard = !!b.view?.hasCard && !useNewCard;
  // A never-trialed Explore-mode user gets their one-time trial on upgrade
  // (card up front); a lapsed/paused user resubscribes (charged today).
  const subMode: "trial" | "resubscribe" = b.view?.isFree ? "trial" : "resubscribe";

  const done = () => {
    onSubscribed?.();
    onClose();
  };

  const oneTapReactivate = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    try {
      if (subMode === "trial") await b.startTrial({ plan_id: pid });
      else await b.subscribeNow({ plan_id: pid });
      done();
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  };

  const emblem = (
    <span
      style={{
        width: 64,
        height: 64,
        borderRadius: ChromeTokens.radius.lg,
        margin: `0 auto ${ChromeTokens.spacing.sm}px`,
        background: `color-mix(in srgb, var(--chrome-color-sky) 50%, var(--chrome-color-base))`,
        display: "grid",
        placeItems: "center",
      }}
    >
      <Icon name={copy.icon} size={30} stroke={ChromeTokens.color.clay} />
    </span>
  );

  const oneTapButton = (
    <button
      type="button"
      onClick={oneTapReactivate}
      disabled={pending}
      aria-label={subMode === "trial" ? "Start free trial" : "Subscribe"}
      style={{
        width: "100%",
        minHeight: 48,
        fontSize: 16,
        fontWeight: 800,
        fontFamily: ChromeTokens.font.ui,
        border: "none",
        borderRadius: ChromeTokens.radius.pill,
        cursor: pending ? "not-allowed" : "pointer",
        background: ChromeTokens.color.sun,
        color: ChromeTokens.color.ink,
        boxShadow: ChromeTokens.shadow.soft,
        opacity: pending ? 0.6 : 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: ChromeTokens.spacing.sm,
      }}
    >
      <Icon name="sparkles" size={17} stroke={ChromeTokens.color.ink} />
      {subMode === "trial"
        ? `Start free trial · then ${money(planById(pid).price)}/mo`
        : `Subscribe · ${money(planById(pid).price)}/mo`}
    </button>
  );

  const cardOnFileLine = (
    <div style={{ fontSize: 12, color: ChromeTokens.color.inkMuted, textAlign: "center" }}>
      Using card ending in {b.view?.card?.last4 ?? "••••"} ·{" "}
      <button
        type="button"
        onClick={() => setUseNewCard(true)}
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: ChromeTokens.color.clay, fontWeight: 700, fontSize: 12, textDecoration: "underline" }}
      >
        use a different card
      </button>
    </div>
  );

  // ipad-native-shell 10.7 / R8.1: inside the App Store build nothing here may
  // take a Stripe payment — not Stripe Elements, not the one-tap card-on-file
  // charge, not a "secured by Stripe" line — and App Review rejects an app that
  // offers one. CheckoutPanel is never mounted on this branch, so Stripe.js
  // (`loadStripe`, first called from CheckoutPanel) never loads. The App Store
  // purchase (10.3) lives in AppStorePlans, which keeps 10.7's no-purchase copy
  // until IAP is configured; it never points at a web purchase (guideline 3.1.1).
  const storeManaged = !billingPlatform().manages;
  // The web's trial sheet (option 2): the plain line and "Keep playing free". The iPad's
  // App Store sheet and a lapsed family's resubscribe keep their own words.
  const trialSheet = !storeManaged && subMode === "trial";
  const subLine = trialSheet ? TRIAL_LINE : copy.sub;
  const checkoutBlock = storeManaged ? (
    <AppStorePlans onUnlocked={() => { onSubscribed?.(); onClose(); }} onRestored={() => onClose()} />
  ) : hasCard ? (
    <div style={{ marginTop: ChromeTokens.spacing.lg, display: "flex", flexDirection: "column", gap: ChromeTokens.spacing.sm }}>
      {oneTapButton}
      {cardOnFileLine}
      <StripeSecured />
    </div>
  ) : (
    <div style={{ marginTop: ChromeTokens.spacing.lg }}>
      <CheckoutPanel planId={pid} mode={subMode} showPlanPicker={false} onPlan={setPid} onDone={done} footer={footer} />
    </div>
  );

  const eyebrow = (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: ChromeTokens.spacing.xs + 2,
        fontSize: 12,
        fontWeight: 800,
        color: ChromeTokens.color.clay,
        textTransform: "uppercase",
        letterSpacing: ".06em",
      }}
    >
      <Icon name={copy.icon} size={14} stroke={ChromeTokens.color.clay} /> {copy.eyebrow}
    </span>
  );

  const maybeLater = (
    <button
      type="button"
      onClick={onClose}
      style={{ background: "none", border: "none", cursor: "pointer", color: ChromeTokens.color.inkMuted, fontFamily: ChromeTokens.font.ui, fontSize: 13, fontWeight: 700, marginTop: ChromeTokens.spacing.md, minHeight: 44 }}
    >
      Maybe later — keep looking around
    </button>
  );

  // Quieter than the trial button on purpose. It is never a submit: a card typed above is
  // dropped with the sheet, and nothing reaches Stripe.
  const keepPlaying = (
    <button
      type="button"
      onClick={onClose}
      style={{
        width: "100%",
        minHeight: 44,
        padding: `${ChromeTokens.spacing.xs}px ${ChromeTokens.spacing.md}px`,
        fontFamily: ChromeTokens.font.ui,
        border: "none",
        background: "none",
        cursor: "pointer",
        color: ChromeTokens.color.ink,
        fontSize: 14,
        fontWeight: 700,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1.3,
      }}
    >
      {KEEP_PLAYING}
      <span style={{ fontSize: 12, fontWeight: 600, color: ChromeTokens.color.inkMuted }}>{KEEP_PLAYING_LINE}</span>
      {savesForLater && (
        <span style={{ fontSize: 12, fontWeight: 600, color: ChromeTokens.color.inkMuted }}>{SAVE_FOR_LATER_LINE}</span>
      )}
    </button>
  );
  const wayOut = trialSheet ? keepPlaying : maybeLater;
  // Always in view: the card form scrolls above it, with or without a phone's keyboard.
  const pinned = (
    <div
      style={{
        flexShrink: 0,
        paddingTop: ChromeTokens.spacing.md,
        display: "flex",
        flexDirection: "column",
        gap: ChromeTokens.spacing.xs,
        background: ChromeTokens.color.base,
      }}
    >
      <div ref={setFooter} />
      {wayOut}
    </div>
  );

  // ── Variant: split — sell on the left, checkout on the right ──
  if (variant === "split") {
    return (
      <PanelSurface open={open} anchor="center" onDismiss={onClose} ariaLabel={copy.title}>
        <div
          data-wbm-rise
          style={{
            width: "min(760px, 86vw)",
            maxHeight: "84vh",
            overflow: "auto",
            display: "grid",
            gridTemplateColumns: "minmax(0,1fr) minmax(0,1.05fr)",
            gap: ChromeTokens.spacing.xl,
            fontFamily: ChromeTokens.font.ui,
            color: ChromeTokens.color.ink,
            animation: `wbm-billing-rise 320ms ${ChromeTokens.motion.softEase}`,
          }}
        >
          {/* sell side */}
          <div style={{ display: "flex", flexDirection: "column", gap: ChromeTokens.spacing.md }}>
            {eyebrow}
            <h2 style={{ fontFamily: ChromeTokens.font.display, fontSize: 30, margin: 0, lineHeight: 1.06 }}>{copy.title}</h2>
            <p style={{ color: ChromeTokens.color.inkMuted, fontSize: 14, lineHeight: 1.5, margin: 0 }}>{subLine}</p>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: ChromeTokens.spacing.sm + 1 }}>
              {SELL_POINTS.map((t) => (
                <li key={t} style={{ display: "flex", gap: 9, alignItems: "center", fontSize: 13.5, fontWeight: 600 }}>
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: ChromeTokens.radius.pill,
                      background: `color-mix(in srgb, var(--chrome-color-grass) 32%, var(--chrome-color-base))`,
                      display: "grid",
                      placeItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon name="check" size={13} stroke={ChromeTokens.color.clay} />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
          {/* checkout side */}
          <div style={{ overflow: "auto" }}>
            {!storeManaged && (
              <>
                <h3 style={{ margin: `0 0 ${ChromeTokens.spacing.md}px`, fontSize: 17 }}>Choose a plan</h3>
                <PlanPicker value={pid} onChange={setPid} compact />
              </>
            )}
            {checkoutBlock}
            {pinned}
          </div>
        </div>
      </PanelSurface>
    );
  }

  // ── Variant: cocoon — centered storybook moment ──
  return (
    <PanelSurface open={open} anchor="center" onDismiss={onClose} ariaLabel={copy.title}>
      <div
        data-wbm-rise
        style={{
          width: "min(420px, 86vw)",
          maxHeight: `calc(100dvh - var(--keyboard-h, 0px) - ${32 + 2 * ChromeTokens.spacing.xl}px)`,
          display: "flex",
          flexDirection: "column",
          textAlign: "center",
          fontFamily: ChromeTokens.font.ui,
          color: ChromeTokens.color.ink,
          animation: `wbm-billing-rise 320ms ${ChromeTokens.motion.softEase}`,
        }}
      >
        <div style={{ flex: "1 1 auto", minHeight: 0, overflow: "auto" }}>
        {emblem}
        {eyebrow}
        <h2 style={{ fontFamily: ChromeTokens.font.display, fontSize: 32, margin: `${ChromeTokens.spacing.sm}px 0`, lineHeight: 1.05 }}>{copy.title}</h2>
        <p style={{ color: ChromeTokens.color.inkMuted, fontSize: 14.5, lineHeight: 1.5, margin: `0 auto ${ChromeTokens.spacing.lg}px`, maxWidth: 340 }}>{subLine}</p>
        {!storeManaged && (
          <div style={{ textAlign: "left" }}>
            <PlanPicker value={pid} onChange={setPid} compact />
          </div>
        )}
        {checkoutBlock}
        </div>
        {pinned}
      </div>
    </PanelSurface>
  );
}
